import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/axios';
import enhancedApi from '../../utils/enhancedApi';
import { performanceMonitor } from '../../utils/performanceMonitor';

/**
 * Fetch companies with enhanced caching to optimize performance
 * The 250+ second response time indicates this needs aggressive caching
 */
export const fetchCompanies = createAsyncThunk('companies/fetchCompanies', async (options = {}, thunkAPI) => {
    try {
        // Set a long cache time (24 hours) for this expensive endpoint
        const cacheTTL = 24 * 60 * 60 * 1000; // 24 hours
        
        performanceMonitor.start('fetchCompanies');
        
        // Use enhanced API with aggressive caching
        const data = await enhancedApi.get('/api/companies/fetchcompanies', {
            cacheTTL,
            forceRefresh: options.forceRefresh || false,
            params: {
                forceRefresh: options.forceRefresh ? 'true' : 'false'
            },
            useDeduplication: true
        });
        
        performanceMonitor.end('fetchCompanies');
        return data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
})

const companySlice = createSlice({
    name: "companies",
    initialState: {
        companies: [],
        loading: false,
        error: null
    },
    reducers: {
        // Set demo data for onboarding
        setDemoData: (state, action) => {
            state.companies = action.payload;
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCompanies.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchCompanies.fulfilled, (state, action) => {
                state.companies = action.payload
                state.loading = false
            })
            .addCase(fetchCompanies.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to fetch companies';
            })
    }
})

export const selectCompanies = (state) => state.companies;

export default companySlice.reducer;